import "bootstrap/dist/css/bootstrap.min.css";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import * as Icon from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { cachedMasterDataService } from "../../service/cachedMasterData.service";
import { ListItem } from "../Controls/AutocompleteInput";

const LandingPage = () => {
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const checkInRef = useRef<HTMLInputElement>(null);
  const checkOutRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null] | null>(
    null
  );

  const [cities, setCities] = useState<ListItem[]>([]);
  const [cityInput, setCityInput] = useState("");
  const [selectedCity, setSelectedCity] = useState<ListItem | null>(null);
  const [filteredCities, setFilteredCities] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);

  const [roomTypes, setRoomTypes] = useState<ListItem[]>([]);
  const [roomTypeInput, setRoomTypeInput] = useState("");
  const [selectedRoomType, setSelectedRoomType] = useState<ListItem | null>(
    null
  );
  const [filteredRoomTypes, setFilteredRoomTypes] = useState<string[]>([]);
  const [showRoomTypeDropdown, setShowRoomTypeDropdown] = useState(false);
  const [highlightedRoomTypeIndex, setHighlightedRoomTypeIndex] = useState(-1);
  const toastRef = useRef<Toast>(null);

  // Track if data has been loaded to prevent duplicate API calls
  const [citiesLoaded, setCitiesLoaded] = useState(false);
  const [roomTypesLoaded, setRoomTypesLoaded] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);

  useEffect(() => {
    if (!citiesLoaded && !loadingCities) {
      setLoadingCities(true);
      cachedMasterDataService
        .getCitiesList()
        .then((data) => {
          console.log("Fetched cities (cached):", data);
          setCities(data ?? []);
          setCitiesLoaded(true);
          setLoadingCities(false);
        })
        .catch(() => {
          setLoadingCities(false);
        });
    }

    if (!roomTypesLoaded && !loadingRoomTypes) {
      setLoadingRoomTypes(true);
      cachedMasterDataService
        .getRoomTypesList()
        .then((data) => {
          console.log("Fetched room types (cached):", data);
          setRoomTypes(data ?? []);
          setRoomTypesLoaded(true);
          setLoadingRoomTypes(false);
        })
        .catch(() => {
          setLoadingRoomTypes(false);
        });
    }
  }, [citiesLoaded, roomTypesLoaded, loadingCities, loadingRoomTypes]); // Fixed dependencies to prevent duplicate calls

  useEffect(() => {
    // Don't show dropdown if a city is already selected and input matches selected city
    if (selectedCity && cityInput === selectedCity.name) {
      setFilteredCities([]);
      setShowDropdown(false);
      setHighlightedIndex(-1);
      return;
    }

    if (cityInput.trim() === "") {
      setFilteredCities(showDropdown ? cities.map((c) => c.name) : []);
      setHighlightedIndex(showDropdown && cities.length > 0 ? 0 : -1);
    } else {
      const filtered = cities
        .map((c) => c.name)
        .filter((name) => name.toLowerCase().includes(cityInput.toLowerCase()));
      setFilteredCities(filtered);
      setShowDropdown(filtered.length > 0 && !selectedCity);
      setHighlightedIndex(filtered.length > 0 ? 0 : -1);
    }
  }, [cityInput, cities, showDropdown, selectedCity]);

  useEffect(() => {
    if (roomTypeInput.trim() === "") {
      setFilteredRoomTypes([]);
      setShowRoomTypeDropdown(false);
      setHighlightedRoomTypeIndex(-1);
    } else {
      const filtered = roomTypes
        .map((rt) => rt.name)
        .filter((name) =>
          name.toLowerCase().includes(roomTypeInput.toLowerCase())
        );
      setFilteredRoomTypes(filtered);
      setShowRoomTypeDropdown(filtered.length > 0);
      setHighlightedRoomTypeIndex(filtered.length > 0 ? 0 : -1);
    }
  }, [roomTypeInput, roomTypes]);

  const handleCitySelect = (name: string) => {
    const city = cities.find((c) => c.name === name);
    setSelectedCity(city || null);
    setCityInput(name);

    // Immediately close dropdown and clear state
    setShowDropdown(false);
    setHighlightedIndex(-1);
    setFilteredCities([]);

    // Remove focus to prevent reopening
    setTimeout(() => {
      (document.activeElement as HTMLElement)?.blur();
    }, 0);
  };

  const handleRoomTypeSelect = (name: string) => {
    setRoomTypeInput(name);
    setShowRoomTypeDropdown(false);
    setHighlightedRoomTypeIndex(-1);
  };

  const handleCityInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || filteredCities.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((idx) =>
        idx < filteredCities.length - 1 ? idx + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((idx) =>
        idx > 0 ? idx - 1 : filteredCities.length - 1
      );
    } else if (e.key === "Enter") {
      if (highlightedIndex >= 0 && highlightedIndex < filteredCities.length) {
        handleCitySelect(filteredCities[highlightedIndex]);
        // Immediately blur to close dropdown and prevent double enter
        (document.activeElement as HTMLElement)?.blur();
      }
    }
  };

  const handleRoomTypeInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (!showRoomTypeDropdown || filteredRoomTypes.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedRoomTypeIndex((idx) =>
        idx < filteredRoomTypes.length - 1 ? idx + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedRoomTypeIndex((idx) =>
        idx > 0 ? idx - 1 : filteredRoomTypes.length - 1
      );
    } else if (e.key === "Enter") {
      if (
        highlightedRoomTypeIndex >= 0 &&
        highlightedRoomTypeIndex < filteredRoomTypes.length
      ) {
        handleRoomTypeSelect(filteredRoomTypes[highlightedRoomTypeIndex]);
        (document.activeElement as HTMLElement)?.blur();
      }
    }
  };

  const handleCheckInFocus = () => {
    if (checkInRef.current) checkInRef.current.type = "date";
  };
  const handleCheckInBlur = () => {
    if (checkInRef.current && !checkIn) checkInRef.current.type = "text";
  };
  const handleCheckOutFocus = () => {
    if (checkOutRef.current) checkOutRef.current.type = "date";
  };
  const handleCheckOutBlur = () => {
    if (checkOutRef.current && !checkOut) checkOutRef.current.type = "text";
  };

  // Helper to get selected city and room type IDs
  const getSelectedCityId = () => {
    const city = cities.find(
      (c) => c.name === cityInput || c.name === selectedCity?.name
    );
    return city ? city.id : "";
  };
  const getSelectedCityName = () => {
    const city = cities.find(
      (c) => c.name === cityInput || c.name === selectedCity?.name
    );
    return city ? city.name : "";
  };

  const getSelectedRoomTypeId = () => {
    const roomType = roomTypes.find((rt) => rt.name === roomTypeInput);
    return roomType ? roomType.id : "";
  };

  const getSelectedRoomTypeName = () => {
    const roomType = roomTypes.find((rt) => rt.name === selectedRoomType?.name);
    return roomType ? roomType.id : "";
  };

  // Helper to check if search is allowed
  const isSearchEnabled = !!selectedCity && !!selectedRoomType;

  const handleSearch = () => {
    const missingFields: string[] = [];

    if (!selectedCity) missingFields.push("City");
    if (!selectedRoomType) missingFields.push("Room Type");
    if (!checkIn || !checkOut) missingFields.push("Check-In and Check-Out");

    if (missingFields.length > 0) {
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "warning",
        title:
          missingFields.length === 3
            ? "Please fill required fields."
            : `Please select: ${missingFields.join(", ")}`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
      return;
    }
    const cityId = getSelectedCityId();
    const cityName = getSelectedCityName();
    const roomTypeId = getSelectedRoomTypeName();
    console.log("roomtype", roomTypeId);
    console.log("Search Params:", {
      cityId,
      cityName,
      roomTypeId,
      checkIn,
      checkOut,
    });
    navigate("/b2b-property", {
      state: { cityId, cityName, roomTypeId, checkIn, checkOut },
    });
  };

  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Make sure Toast is rendered at the top level of your component's JSX:
  return (
    <div>
      <Toast ref={toastRef} />
      {/* HERO SECTION */}
      <div className="landing-hero-section">
        <div className="container">
          <div className="hero-overlay"></div>
          <div className="rounded-text-white position-relative pt-5">
            <div className="position-relative z-2 d-flex gap-4 flex-column justify-content-start align-items-start">
              <h1 className="tx-54 _600 mb-4">
                Your Trusted Partner for <br />
                Corporate housing.
              </h1>
              <p className="text-light mt-4 tx-20 text-star">
                From apart-hotels to co-living spaces in the premium luxury and
                budget categories, IOS offers every type of stay. We ensure
                every stay feels exceptional. Trust us to make your stay
                effortless, exclusive, and unforgettable.
              </p>
              <div className="hero-searchbar d-flex  gap-3 w-100">
                <div className="searchbar-control d-flex flex-row align-items-center gap-2 w-100">
                  <label htmlFor="location" className="mb-0 flex-shrink-0">
                    Search by Location
                  </label>
                  <div className="avatar avatar-md rounded-circle">
                    <span className="icon-box border tx-22">
                      {" "}
                      <Icon.GeoAlt className="text-muted" />{" "}
                    </span>
                  </div>
                  <div className="position-relative w-100">
                    <InputText
                      value={cityInput}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setCityInput(e.target.value);
                        if (selectedCity) {
                          setSelectedCity(null);
                        }
                      }}
                      onKeyDown={handleCityInputKeyDown}
                      onFocus={() => {
                        // Only show dropdown if no city is selected or input doesn't match selected city
                        if (!selectedCity || cityInput !== selectedCity.name) {
                          if (cityInput.trim() === "") {
                            setFilteredCities(cities.map((c) => c.name));
                            setShowDropdown(true);
                          } else {
                            const filtered = cities
                              .map((c) => c.name)
                              .filter((name) =>
                                name
                                  .toLowerCase()
                                  .includes(cityInput.toLowerCase())
                              );
                            if (filtered.length > 0) {
                              setFilteredCities(filtered);
                              setShowDropdown(true);
                            }
                          }
                        }
                      }}
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        // Only hide dropdown if focus is not moving to dropdown items
                        if (
                          !e.relatedTarget ||
                          !e.relatedTarget.closest?.(".autocomplete-dropdown")
                        ) {
                          setShowDropdown(false);
                        }
                      }}
                      placeholder="Search City"
                      id="location"
                      name="location"
                      className={`w-100 ${
                        selectedCity ? "border-success" : ""
                      }`}
                      autoComplete="off"
                    />
                    {selectedCity && (
                      <button
                        type="button"
                        className="btn-close position-absolute"
                        style={{
                          right: "8px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "12px",
                        }}
                        onClick={() => {
                          setCityInput("");
                          setSelectedCity(null);
                          setFilteredCities([]);
                          setShowDropdown(false);
                        }}
                      ></button>
                    )}
                    {showDropdown && filteredCities.length > 0 && (
                      <div
                        className="autocomplete-dropdown position-absolute w-100 bg-white border rounded shadow-sm"
                        style={{
                          top: "100%",
                          zIndex: 9999,
                          maxHeight: "200px",
                          overflowY: "auto",
                          backgroundColor: "white",
                        }}
                        onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking dropdown
                      >
                        {filteredCities.map((cityName, index) => (
                          <div
                            key={cityName}
                            className={`autocomplete-item p-2`}
                            onClick={() => handleCitySelect(cityName)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            style={{
                              cursor: "pointer",
                              backgroundColor:
                                index === highlightedIndex
                                  ? "#f8f9fa"
                                  : "white",
                              color: "#333",
                              borderBottom: "1px solid #eee",
                              padding: "8px 12px",
                              fontSize: "14px",
                              fontWeight: "400",
                            }}
                          >
                            {cityName}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="searchbar-control d-flex flex-row align-items-center gap-2 w-100">
                  <label htmlFor="checkin" className="mb-0 flex-shrink-0">
                    Checkin-Checkout Date
                  </label>
                  <div className="avatar avatar-md rounded-circle">
                    <span className="icon-box border tx-22">
                      {" "}
                      <Icon.CalendarRange className="text-muted" />{" "}
                    </span>
                  </div>
                  <Calendar
                    value={dateRange}
                    onChange={(e) => {
                      setDateRange(e.value as [Date | null, Date | null]);
                      if (Array.isArray(e.value)) {
                        setCheckIn(
                          e.value[0] ? formatLocalDate(e.value[0]) : ""
                        );
                        setCheckOut(
                          e.value[1] ? formatLocalDate(e.value[1]) : ""
                        );
                      } else {
                        setCheckIn("");
                        setCheckOut("");
                      }
                    }}
                    selectionMode="range"
                    readOnlyInput
                    placeholder="Check-In & Check-Out"
                    dateFormat="dd-mm-yy"
                    showIcon
                    hideOnRangeSelection
                    minDate={new Date()}
                    numberOfMonths={2} // Show two months side by side
                    view="date"
                    touchUI={false}
                    style={{ width: "100%" }}
                    className="w-100"
                  />
                </div>
                <div className="searchbar-control d-flex flex-row align-items-center gap-2 w-100">
                  <label htmlFor="roomType" className="mb-0 flex-shrink-0">
                    Search by Room Type
                  </label>
                  <div className="avatar avatar-md rounded-circle">
                    <span className="icon-box border tx-22">
                      {" "}
                      <Icon.Buildings className="text-muted" />{" "}
                    </span>
                  </div>
                  <Dropdown
                    value={selectedRoomType ? selectedRoomType.id : null}
                    options={roomTypes}
                    onChange={(e) => {
                      const roomType = roomTypes.find(
                        (rt) => rt.id === e.value
                      );
                      setSelectedRoomType(roomType || null);
                    }}
                    placeholder="Room type"
                    id="roomType"
                    name="roomType"
                    appendTo="self"
                    optionLabel="name"
                    optionValue="id"
                    className="w-100"
                  />
                </div>
                <div
                  className="searchbar-control d-flex align-items-end w-100"
                  style={{ maxWidth: "200px" }}
                >
                  <Button
                    onClick={handleSearch}
                    label="Search"
                    className="p-button-secondary"
                    style={{
                      width: "100%",
                      height: 48,
                      fontWeight: 200,
                      fontSize: 14,
                      borderRadius: 8,
                      border: "none",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
